import { useState } from 'react'
import { useAtom } from 'jotai'
import Button from '@mui/material/Button'
import DiscountRoundedIcon from '@mui/icons-material/DiscountRounded'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import Alert from '@mui/material/Alert'

import type { YearlyFeeDiscountAssignInMunicipalityResponse } from '@/types'
import { discountAssignInMunicipalityJobIdAtom } from '@/state'
import { useYearlyFeeDiscountAssignInMunicipality } from '@/hooks/api/use-yearly-fee-discount'
import YearlyFeeDiscountFilter from '@/components/filters/yearly-fee-discount-filter'

export default function DiscountAssignInMunicipality() {

    const [discountAssignInMunicipalityJobId, setDiscountAssignInMunicipalityJobId] = useAtom(discountAssignInMunicipalityJobIdAtom)
    const [requestInProgress, setRequestInProgress] = useState<boolean>(false)
    const [error, setError] = useState<string | null>(null)

    // Action dialogs
    const [confirm, setConfirm] = useState<boolean>(false)

    const [discountId, setDiscountId] = useState<number | null>(null)

    const successCallback = (data: YearlyFeeDiscountAssignInMunicipalityResponse) => {
        if (data.id) {
            setDiscountAssignInMunicipalityJobId(data.id)
        }
    }

    const mutation = useYearlyFeeDiscountAssignInMunicipality(
        {
            onSuccessCallback: successCallback,
            setRequestInProgress,
            onErrorCallback(errorMessage) {
                setError(errorMessage)
            },
        },
    )

    const handleClickGenerate = () => {
        setRequestInProgress(true)
        mutation.mutate(discountId)
        setConfirm(false)
    }

    return (
        <>
            <Button
                color="terciary"
                variant="outlined"
                startIcon={<DiscountRoundedIcon />}
                onClick={() => setConfirm(true)}
                disabled={requestInProgress || !!discountAssignInMunicipalityJobId}
                sx={{
                    backgroundColor: 'errorRedLight.light',
                    borderColor: 'errorRedLight.dark',
                }}
            >
                Aplikovat slevu v obci
            </Button>
            <Dialog
                open={confirm}
                onClose={() => setConfirm(false)}
            >
                <DialogContent>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}>
                        {error &&
                            <Alert
                                severity="success"
                                sx={(theme) => ({
                                    flexGrow: 1,
                                    margin: theme.spacing(1, 0),
                                })}
                                action={
                                    <Button
                                        color="success"
                                        size="small"
                                        onClick={() => setError(null)}
                                    >
                                        Zavřít
                                    </Button>
                                }
                            >
                                {error}
                            </Alert>
                        }
                        <Typography variant="h5" component="div" mb={2}>
                            Aplikování slevy v celé obci
                        </Typography>
                        <Typography variant="body2" component="div" mb={2}>
                            Tato akce přiřadí slevu všem odpovídajícím poplatníkům. Hromadně lze přiřadit jen slevy vázané na věk a slevy za účast v motivačním modelu.
                        </Typography>
                        <Box sx={{
                            p: 2,
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 2,
                            justifyContent: 'center',
                        }}>
                            <YearlyFeeDiscountFilter
                                selectedId={discountId}
                                setSelectedId={setDiscountId}
                                label="Vybraná sleva"
                                hideCustom
                                hideNonMass
                            />
                        </Box>
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            flexGrow: 1,
                        }}>
                            <Button
                                variant="text"
                                color="terciary"
                                onClick={() => setConfirm(false)}
                            >
                                Zrušit
                            </Button>
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={requestInProgress ? <CircularProgress color="inherit" size={20} /> : <CheckCircleRoundedIcon />}
                                onClick={() => handleClickGenerate()}
                                disabled={requestInProgress || !discountId}
                            >
                                Aplikovat
                            </Button>
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    )
}
